import React, { useState, useEffect, useRef } from 'react';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import { useSelector } from 'react-redux';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button, 
  Typography,
  Box,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import { message } from 'antd';
import { 
  FormatBold, 
  FormatItalic, 
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatColorFill,
  FormatColorText,
  Add,
  FileUpload,
  FileDownload,
  Undo,
  Redo,
  ContentCopy,
  ContentPaste,
  Delete,
  CallMerge,
  CallSplit,
  BorderAll,
  BorderOuter,
  BorderInner,
  BorderTop,
  BorderBottom,
  BorderLeft,
  BorderRight,
  BorderClear,
  Save,
  DriveFileMove as DriveFileMoveIcon,
  Folder as FolderIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import FolderDialog from './components/FolderDialog';
import './SpreadsheetToolbar.css';

const SpreadsheetToolbar = ({ readOnly = false, onSave }) => {
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  
  // Token'ı localStorage'dan da kontrol et
  const getAppToken = () => {
    // Önce Redux store'dan al
    if (appToken && appToken !== 'null') return appToken;
    try {
      const storedToken = localStorage.getItem('appToken');
      if (storedToken && storedToken !== 'null') return storedToken;
      const applicationToken = localStorage.getItem('applicationToken');
      if (applicationToken && applicationToken !== 'null') {
        try {
          const parsed = JSON.parse(applicationToken);
          if (typeof parsed === 'string' && parsed !== 'null') return parsed;
        } catch {
          if (applicationToken !== 'null') return applicationToken;
        }
      }
    } catch (error) {
      console.error('Token alma hatası:', error);
    }
    return null;
  };
  
  const {
    getActiveSheet,
    updateCellFormat,
    copySelection,
    pasteSelection,
    exportExcel,
    importExcel,
    saveTableToAPI,
    mergeCells,
    unmergeCells,
    isMergedParent,
    getActiveSheet: getCurrentSheet
  } = useSpreadsheetStore();

  const [showColorPicker, setShowColorPicker] = useState(null);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBorderMenu, setShowBorderMenu] = useState(false);
  const [borderColor, setBorderColor] = useState('#000000');
  const [borderStyle, setBorderStyle] = useState('solid');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [tableTitle, setTableTitle] = useState('');
  const [tableDescription, setTableDescription] = useState('');
  const [selectedFolders, setSelectedFolders] = useState(['0']); // Array olarak değiştirdik
  const [folderOptions, setFolderOptions] = useState([]);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const borderMenuRef = useRef(null);

  // Klasör listesini yükle
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const token = getAppToken();
        const userDataObj = userData || JSON.parse(localStorage.getItem('userData') || '{}');
        if (!token || !userDataObj?.id) {
          setFolderOptions([]);
          return;
        }
        const response = await fetch('https://app.hhoms.com/api/form/Categories/parent/list', {
          headers: {
            'ApplicationToken': token,
            'User': userDataObj.id,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        console.log('📂 API Response Data:', data);
        console.log('📂 formCategories:', data.formCategories);
        
        // Klasör yapısını düzleştir
        const flattenFolders = (categories, level = 0, parentId = 0) => {
          let result = [];
          categories.forEach(cat => {
            console.log('📂 FLATTEN DEBUG - Processing category:', cat);
            result.push({
              value: cat.id?.toString() || '0',
              label: cat.title,
              parentId: parentId
            });
            if (Array.isArray(cat.detail) && cat.detail.length > 0) {
              result = result.concat(flattenFolders(cat.detail, level + 1, cat.id));
            }
          });
          return result;
        };
        
        let options = [];
        if (Array.isArray(data.formCategories)) {
          options = flattenFolders(data.formCategories);
        }
        console.log('📂 Final folderOptions:', options);
        setFolderOptions(options);
      } catch (error) {
        console.error('Klasörler yüklenirken hata:', error);
        setFolderOptions([]);
      }
    };
    fetchFolders();
  }, []);

  const activeSheet = getCurrentSheet();
  const activeCell = activeSheet?.activeCell || { row: 0, col: 0 };
  const selection = activeSheet?.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 };
  
  // Border menüsünü dışarı tıklayınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (borderMenuRef.current && !borderMenuRef.current.contains(event.target)) {
        setShowBorderMenu(false);
      }
    };
    
    if (showBorderMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBorderMenu]);
  
  // Dialog açıkken copy-paste işlemlerini etkinleştir
  useEffect(() => {
    const handleDialogKeyDown = (e) => {
      // Dialog açıksa ve Ctrl+C, Ctrl+V gibi işlemler için event propagation'ı durdur
      if (saveDialogOpen && e.ctrlKey && ['c', 'v', 'x', 'a', 'z', 'y'].includes(e.key.toLowerCase())) {
        // Bu işlemlerin input alanlarında çalışmasına izin ver
        if (document.activeElement && (
          document.activeElement.tagName === 'INPUT' || 
          document.activeElement.tagName === 'TEXTAREA'
        )) {
          e.stopPropagation();
        }
      }
    };
    
    if (saveDialogOpen) {
      document.addEventListener('keydown', handleDialogKeyDown, true); // capture phase'de yakala
    }
    
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown, true);
    };
  }, [saveDialogOpen]);
  
  // Seçili hücrelerin format bilgisini al
  const getSelectedCellFormat = () => {
    // Selection ve activeSheet kontrolü
    if (!selection || !activeSheet || !activeSheet.grid) {
      return {
        bold: false,
        italic: false,
        underline: false,
        fontSize: 11,
        fontFamily: 'Arial',
        textColor: '#000000',
        backgroundColor: '#ffffff',
        horizontalAlign: 'left',
        verticalAlign: 'top',
        border: {
          top: { style: 'none', color: '#000000' },
          right: { style: 'none', color: '#000000' },
          bottom: { style: 'none', color: '#000000' },
          left: { style: 'none', color: '#000000' }
        }
      };
    }

    // ActiveCell kontrolü
    if (!activeCell || typeof activeCell.row !== 'number' || typeof activeCell.col !== 'number') {
      return {
        bold: false,
        italic: false,
        underline: false,
        fontSize: 11,
        fontFamily: 'Arial',
        textColor: '#000000',
        backgroundColor: '#ffffff',
        horizontalAlign: 'left',
        verticalAlign: 'top',
        border: {
          top: { style: 'none', color: '#000000' },
          right: { style: 'none', color: '#000000' },
          bottom: { style: 'none', color: '#000000' },
          left: { style: 'none', color: '#000000' }
        }
      };
    }
    
    // Tek hücre seçiliyse
    if (selection.startRow === selection.endRow && selection.startCol === selection.endCol) {
      return activeSheet.grid[activeCell.row]?.[activeCell.col]?.format || {
        bold: false,
        italic: false,
        underline: false,
        fontSize: 11,
        fontFamily: 'Arial',
        textColor: '#000000',
        backgroundColor: '#ffffff',
        horizontalAlign: 'left',
        verticalAlign: 'top',
        border: {
          top: { style: 'none', color: '#000000' },
          right: { style: 'none', color: '#000000' },
          bottom: { style: 'none', color: '#000000' },
          left: { style: 'none', color: '#000000' }
        }
      };
    }
    
    // Çoklu hücre seçiliyse - ilk hücrenin formatını al
    return activeSheet.grid[selection.startRow]?.[selection.startCol]?.format || {
      bold: false,
      italic: false,
      underline: false,
      fontSize: 11,
      fontFamily: 'Arial',
      textColor: '#000000',
      backgroundColor: '#ffffff',
      horizontalAlign: 'left',
      verticalAlign: 'top',
      border: {
        top: { style: 'none', color: '#000000' },
        right: { style: 'none', color: '#000000' },
        bottom: { style: 'none', color: '#000000' },
        left: { style: 'none', color: '#000000' }
      }
    };
  };

  const cellFormat = getSelectedCellFormat();

  const handleFormatChange = (format) => {
    // Selection kontrolü
    if (!selection || typeof selection.startRow !== 'number' || typeof selection.startCol !== 'number' || 
        typeof selection.endRow !== 'number' || typeof selection.endCol !== 'number') {
      console.warn('SpreadsheetToolbar: Geçersiz selection objesi', selection);
      return;
    }

    // Seçili tüm hücrelere format uygula
    const { startRow, startCol, endRow, endCol } = selection;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        updateCellFormat(row, col, format);
      }
    }
  };

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      importExcel(file);
    }
  };

  const handleFileExport = () => {
    exportExcel();
  };

  // Bulut kaydetme kaldırıldı

  const handleSaveTable = () => {
    setSaveDialogOpen(true);
  };

    const handleSaveConfirm = async () => {
    console.log('handleSaveConfirm başladı');
    console.log('tableTitle:', tableTitle);
    console.log('tableDescription:', tableDescription);
    console.log('selectedFolders:', selectedFolders);
    console.log('🔥 TABLO KAYDETME - selectedFolders:', selectedFolders);
    
    if (!tableTitle.trim()) {
      message.error('Tablo başlığı gereklidir');
      return;
    }

    if (!selectedFolders || selectedFolders.length === 0) {
      message.error('En az bir klasör seçmelisiniz');
      return;
    }

    try {
      setSaving(true);
      
      // Çoklu klasör için tek kaydetme işlemi yap
      console.log('� TABLO KAYDETME - selectedFolders array:', selectedFolders);
      
      if (onSave) {
        // onSave fonksiyonuna parentId olarak seçilen klasörlerin array'ini ilet
        console.log('📍 TOOLBAR DEBUG - onSave çağrılıyor, selectedFolders:', selectedFolders);
        const result = await onSave(tableTitle.trim(), tableDescription.trim(), selectedFolders);
        
        if (result === true || result?.success === true) {
          message.success(`Tablo ${selectedFolders.length} klasöre başarıyla kaydedildi!`);
          setSaveDialogOpen(false);
          setTableTitle('');
          setTableDescription('');
          setSelectedFolders(['0']);
        } else {
          message.error('Kaydetme başarısız oldu');
        }
      } else {
        // Fallback: eski saveTableToAPI fonksiyonu - array desteklemiyor
        message.error('Çoklu klasör seçimi desteklenmiyor');
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      message.error(`Kaydetme sırasında hata oluştu: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCancel = () => {
    setSaveDialogOpen(false);
    setTableTitle('');
    setTableDescription('');
    setSelectedFolders(['0']);
  };

  const handleFolderDialogOpen = () => {
    setFolderDialogOpen(true);
  };

  const handleFolderDialogClose = () => {
    setFolderDialogOpen(false);
  };

  const handleCopy = () => {
    copySelection();
  };

  const handlePaste = () => {
    pasteSelection(activeCell.row, activeCell.col);
  };

  const handleUndo = () => {
    // TODO: Implement undo
  };

  const handleRedo = () => {
    // TODO: Implement redo
  };

  const handleMergeCells = () => {
    const { selection } = activeSheet;
    if (selection.startRow === selection.endRow && selection.startCol === selection.endCol) {
      return; // Tek hücre seçili
    }
    
    mergeCells(selection.startRow, selection.startCol, selection.endRow, selection.endCol);
  };

  const handleUnmergeCells = () => {
    const { activeCell } = activeSheet;
    if (isMergedParent(activeCell.row, activeCell.col)) {
      unmergeCells(activeCell.row, activeCell.col);
    }
  };

  // Border uygulama fonksiyonları
  const applyBorder = (borderType) => {
    const { selection } = activeSheet;
    const { startRow, startCol, endRow, endCol } = selection;
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    
    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        const currentFormat = activeSheet.grid[row]?.[col]?.format || {};
        let newBorder = { ...currentFormat.border };
        
        switch (borderType) {
          case 'all':
            newBorder = {
              top: { style: borderStyle, color: borderColor },
              right: { style: borderStyle, color: borderColor },
              bottom: { style: borderStyle, color: borderColor },
              left: { style: borderStyle, color: borderColor }
            };
            break;
          case 'outer':
            if (row === minRow) newBorder.top = { style: borderStyle, color: borderColor };
            if (row === maxRow) newBorder.bottom = { style: borderStyle, color: borderColor };
            if (col === minCol) newBorder.left = { style: borderStyle, color: borderColor };
            if (col === maxCol) newBorder.right = { style: borderStyle, color: borderColor };
            break;
          case 'inner':
            if (row !== minRow) newBorder.top = { style: borderStyle, color: borderColor };
            if (row !== maxRow) newBorder.bottom = { style: borderStyle, color: borderColor };
            if (col !== minCol) newBorder.left = { style: borderStyle, color: borderColor };
            if (col !== maxCol) newBorder.right = { style: borderStyle, color: borderColor };
            break;
          case 'top':
            if (row === minRow) newBorder.top = { style: borderStyle, color: borderColor };
            break;
          case 'bottom':
            if (row === maxRow) newBorder.bottom = { style: borderStyle, color: borderColor };
            break;
          case 'left':
            if (col === minCol) newBorder.left = { style: borderStyle, color: borderColor };
            break;
          case 'right':
            if (col === maxCol) newBorder.right = { style: borderStyle, color: borderColor };
            break;
          case 'clear':
            newBorder = {
              top: { style: 'none', color: '#000000' },
              right: { style: 'none', color: '#000000' },
              bottom: { style: 'none', color: '#000000' },
              left: { style: 'none', color: '#000000' }
            };
            break;
        }
        
        updateCellFormat(row, col, { border: newBorder });
      }
    }
    
    setShowBorderMenu(false);
  };



  return (
    <div className="spreadsheet-toolbar">
      <div className="toolbar-section">
        <button 
          className={`toolbar-button ${cellFormat.bold ? 'active' : ''}`}
          onClick={() => handleFormatChange({ bold: !cellFormat.bold })}
          title="Kalın (Ctrl+B)"
        >
          <FormatBold />
        </button>
        
        <button 
          className={`toolbar-button ${cellFormat.italic ? 'active' : ''}`}
          onClick={() => handleFormatChange({ italic: !cellFormat.italic })}
          title="İtalik (Ctrl+I)"
        >
          <FormatItalic />
        </button>
        
        <button 
          className={`toolbar-button ${cellFormat.underline ? 'active' : ''}`}
          onClick={() => handleFormatChange({ underline: !cellFormat.underline })}
          title="Altı Çizili (Ctrl+U)"
        >
          <FormatUnderlined />
        </button>
      </div>

      <div className="toolbar-section">
        <button 
          className={`toolbar-button ${cellFormat.horizontalAlign === 'left' ? 'active' : ''}`}
          onClick={() => handleFormatChange({ horizontalAlign: 'left' })}
          title="Sola Hizala"
        >
          <FormatAlignLeft />
        </button>
        
        <button 
          className={`toolbar-button ${cellFormat.horizontalAlign === 'center' ? 'active' : ''}`}
          onClick={() => handleFormatChange({ horizontalAlign: 'center' })}
          title="Ortala"
        >
          <FormatAlignCenter />
        </button>
        
        <button 
          className={`toolbar-button ${cellFormat.horizontalAlign === 'right' ? 'active' : ''}`}
          onClick={() => handleFormatChange({ horizontalAlign: 'right' })}
          title="Sağa Hizala"
        >
          <FormatAlignRight />
        </button>
      </div>

      <div className="toolbar-section">
        <div className="color-picker-container">
          <button 
            className="toolbar-button color-button"
            onClick={() => setShowTextColorPicker(!showTextColorPicker)}
            title="Yazı Rengi"
            style={{ color: cellFormat.textColor || '#000000' }}
          >
            <FormatColorText />
          </button>
          {showTextColorPicker && (
            <div className="color-picker-popup">
              <HexColorPicker
                color={cellFormat.textColor || '#000000'}
                onChange={(color) => {
                  handleFormatChange({ textColor: color });
                  setShowTextColorPicker(false);
                }}
              />
            </div>
          )}
        </div>

        <div className="color-picker-container">
          <button 
            className="toolbar-button color-button"
            onClick={() => setShowBgColorPicker(!showBgColorPicker)}
            title="Arka Plan Rengi"
            style={{ backgroundColor: cellFormat.backgroundColor || '#ffffff' }}
          >
            <FormatColorFill />
          </button>
          {showBgColorPicker && (
            <div className="color-picker-popup">
              <HexColorPicker
                color={cellFormat.backgroundColor || '#ffffff'}
                onChange={(color) => {
                  handleFormatChange({ backgroundColor: color });
                  setShowBgColorPicker(false);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-section">
        <button 
          className="toolbar-button"
          onClick={handleUndo}
          title="Geri Al (Ctrl+Z)"
        >
          <Undo />
        </button>
        
        <button 
          className="toolbar-button"
          onClick={handleRedo}
          title="Yinele (Ctrl+Y)"
        >
          <Redo />
        </button>
      </div>

      <div className="toolbar-section">
        <button 
          className="toolbar-button"
          onClick={handleCopy}
          title="Kopyala (Ctrl+C)"
        >
          <ContentCopy />
        </button>
        
        <button 
          className="toolbar-button"
          onClick={handlePaste}
          title="Yapıştır (Ctrl+V)"
        >
          <ContentPaste />
        </button>
        
        <button 
          className="toolbar-button"
          onClick={() => handleFormatChange({ value: '' })}
          title="Sil (Delete)"
        >
          <Delete />
        </button>
      </div>

      <div className="toolbar-section">
        <button 
          className="toolbar-button"
          onClick={handleMergeCells}
          title="Hücreleri Birleştir"
        >
          <CallMerge />
        </button>
        
        <button 
          className="toolbar-button"
          onClick={handleUnmergeCells}
          title="Hücreleri Ayır"
        >
          <CallSplit />
        </button>
      </div>

      <div className="toolbar-section">
        <div className="border-container" ref={borderMenuRef}>
          <button 
            className="toolbar-button"
            onClick={() => setShowBorderMenu(!showBorderMenu)}
            title="Kenarlık"
          >
            <BorderAll />
          </button>
          {showBorderMenu && (
            <div className="border-menu">
              <div className="border-style-section">
                <label>Stil:</label>
                <select 
                  value={borderStyle} 
                  onChange={(e) => setBorderStyle(e.target.value)}
                  className="border-style-select"
                >
                  <option value="solid">Düz</option>
                  <option value="dotted">Noktalı</option>
                  <option value="dashed">Kesikli</option>
                  <option value="double">Çift</option>
                  <option value="thick">Kalın</option>
                </select>
              </div>
              
              <div className="border-color-section">
                <label>Renk:</label>
                <input 
                  type="color" 
                  value={borderColor} 
                  onChange={(e) => setBorderColor(e.target.value)}
                  className="border-color-input"
                />
              </div>
              
              <div className="border-options">
                <button onClick={() => applyBorder('all')} title="Tüm Kenarlıklar">
                  <BorderAll /> Tümü
                </button>
                <button onClick={() => applyBorder('outer')} title="Dış Kenarlık">
                  <BorderOuter /> Dış
                </button>
                <button onClick={() => applyBorder('inner')} title="İç Kenarlıklar">
                  <BorderInner /> İç
                </button>
                <button onClick={() => applyBorder('top')} title="Üst Kenarlık">
                  <BorderTop /> Üst
                </button>
                <button onClick={() => applyBorder('bottom')} title="Alt Kenarlık">
                  <BorderBottom /> Alt
                </button>
                <button onClick={() => applyBorder('left')} title="Sol Kenarlık">
                  <BorderLeft /> Sol
                </button>
                <button onClick={() => applyBorder('right')} title="Sağ Kenarlık">
                  <BorderRight /> Sağ
                </button>
                <button onClick={() => applyBorder('clear')} title="Kenarlıkları Temizle">
                  <BorderClear /> Temizle
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="toolbar-section">
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileImport}
          style={{ display: 'none' }}
          id="file-import"
        />
        <label htmlFor="file-import" className="toolbar-button" title="Excel Dosyası İçe Aktar">
          <FileUpload />
        </label>
        
        <button 
          className="toolbar-button"
          onClick={handleFileExport}
          title="Excel Dosyası Dışa Aktar"
        >
          <FileDownload />
        </button>
        
        {/* Buluta kaydet butonu kaldırıldı */}
        
        {!readOnly && (
          <button 
            className="toolbar-button"
            onClick={handleSaveTable}
            title="Tabloyu Kaydet"
          >
            <Save />
          </button>
        )}
      </div>

      <div className="toolbar-section">
        <select 
          className="font-family-select"
          value={cellFormat.fontFamily || 'Arial'}
          onChange={(e) => handleFormatChange({ fontFamily: e.target.value })}
        >
          <option value="Arial">Arial</option>
          <option value="Calibri">Calibri</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
        </select>
        
        <select 
          className="font-size-select"
          value={cellFormat.fontSize || 11}
          onChange={(e) => handleFormatChange({ fontSize: parseInt(e.target.value) })}
        >
          {[8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72].map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>

      {/* Kaydetme Dialog'u */}
      <Dialog 
        open={saveDialogOpen} 
        onClose={handleSaveCancel}
        fullWidth
        maxWidth="md"
        PaperProps={{
          elevation: 24,
          sx: {
            maxHeight: '80vh',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
        onKeyDown={(e) => {
          // Dialog içinde Ctrl+C, Ctrl+V vb. işlemlerin çalışması için event propagation'ı durdur
          if (e.ctrlKey && ['c', 'v', 'x', 'a', 'z', 'y'].includes(e.key.toLowerCase())) {
            e.stopPropagation();
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          background: 'linear-gradient(to right, #f7f9fc, #edf2f7)',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Save color="primary" />
          <Typography variant="h6" component="span">
            Tabloyu Kaydet
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 1, 
            bgcolor: 'info.light', 
            color: 'info.contrastText' 
          }}>
            <Typography variant="body2">
              Tablonuzu kaydetmek için gerekli bilgileri girin
            </Typography>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Tablo Başlığı *"
              type="text"
              fullWidth
              variant="outlined"
              value={tableTitle}
              onChange={(e) => setTableTitle(e.target.value)}
              placeholder="Örn: Satış Raporu"
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&:hover fieldset': {
                    borderColor: '#1976d2'
                  }
                }
              }}
              InputProps={{
                style: { 
                  WebkitUserSelect: 'text', 
                  userSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text'
                },
                onKeyDown: (e) => {
                  // Keyboard shortcuts'u engellemek için stop propagation
                  if (e.ctrlKey && ['c', 'v', 'x', 'a', 'z', 'y'].includes(e.key.toLowerCase())) {
                    e.stopPropagation();
                  }
                },
                onPaste: (e) => e.stopPropagation(),
                onCopy: (e) => e.stopPropagation(),
                onCut: (e) => e.stopPropagation()
              }}
            />
            
            <TextField
              margin="dense"
              label="Açıklama (İsteğe bağlı)"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={tableDescription}
              onChange={(e) => setTableDescription(e.target.value)}
              placeholder="Tabloyla ilgili açıklama ekleyin..."
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&:hover fieldset': {
                    borderColor: '#1976d2'
                  }
                }
              }}
              InputProps={{
                style: { 
                  WebkitUserSelect: 'text', 
                  userSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text'
                },
                onKeyDown: (e) => {
                  // Keyboard shortcuts'u engellemek için stop propagation
                  if (e.ctrlKey && ['c', 'v', 'x', 'a', 'z', 'y'].includes(e.key.toLowerCase())) {
                    e.stopPropagation();
                  }
                },
                onPaste: (e) => e.stopPropagation(),
                onCopy: (e) => e.stopPropagation(),
                onCut: (e) => e.stopPropagation()
              }}
            />
            
            {/* Klasör Seçimi */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                  Klasör Seçimi
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<FolderIcon />}
                  onClick={handleFolderDialogOpen}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  Klasör Seç
                </Button>
              </Box>
              
              {selectedFolders && selectedFolders.length > 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 0.5, 
                  p: 1,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: '#e2e8f0',
                  bgcolor: '#ffffff'
                }}>
                  <Typography variant="caption" sx={{ width: '100%', mb: 0.5, color: '#64748b' }}>
                    Seçili Klasörler ({selectedFolders.length}):
                  </Typography>
                  {selectedFolders.map(folderId => {
                    const folder = folderOptions.find(opt => opt.value === folderId) || 
                                  (folderId === '0' ? { label: 'Ana Klasör', value: '0' } : null);
                    return folder ? (
                      <Chip 
                        key={folderId}
                        label={folder.label}
                        size="small"
                        deleteIcon={<CloseIcon fontSize="small" />}
                        onDelete={() => setSelectedFolders(prev => prev.filter(id => id !== folderId))}
                        sx={{
                          fontWeight: 500,
                          borderRadius: '6px',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                          },
                          '& .MuiChip-deleteIcon': {
                            color: '#64748b',
                            '&:hover': {
                              color: '#1976d2'
                            }
                          }
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ 
                  mt: 0.5,
                  p: 1,
                  borderRadius: '8px',
                  border: '1px dashed',
                  borderColor: '#e2e8f0',
                  bgcolor: '#f8fafc',
                  textAlign: 'center'
                }}>
                  Ana klasöre kaydedilecek
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0',
          p: 2.5,
          bgcolor: '#f9fafc'
        }}>
          <Button 
            onClick={handleSaveCancel} 
            disabled={saving}
            variant="outlined"
            sx={{ 
              px: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            İptal
          </Button>
          <Button 
            onClick={handleSaveConfirm} 
            variant="contained" 
            disabled={saving || !tableTitle.trim()}
            sx={{ 
              px: 3,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Klasör Seçim Dialog'u */}
      <FolderDialog
        open={folderDialogOpen}
        onClose={handleFolderDialogClose}
        tableTitle={tableTitle || 'Yeni Tablo'}
        folderOptions={folderOptions}
        selectedFolders={selectedFolders}
        setSelectedFolders={setSelectedFolders}
      />
    </div>
  );
};

export default SpreadsheetToolbar;
